<template>
  <article>
    <activity-stage :customRootContainerClass="'puzzling-letters'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div class="puzzling-letters-container">
          <div class="puzzling-letters-title">
            <h2>{{ currentRound?.answer }}</h2>
          </div>

          <div class="background-image-container">
            <img
              class="background-image"
              :alt="currentRound?.answerImage?.image?.description"
              :src="currentRound?.answerImage?.image?.src"
              :class="{ isMobile }"
            />
          </div>

          <div class="puzzle-letters-container focusable" tabindex="0">
            <div class="puzzle-piece-container absolute-puzzle-01">
              <svg height="220px" class="puzzlepiece01" v-html="puzzlepiece01"></svg>

              <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

              <div class="puzzle-piece">
                <p>{{ currentRound.letter }}</p>
              </div>
            </div>

            <div class="puzzle-piece-container absolute-puzzle-02">
              <svg height="179px" class="puzzlepiece02" v-html="puzzlepiece02"></svg>

              <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

              <div class="puzzle-piece">
                <p>{{ currentRound.letter }}</p>
              </div>
            </div>

            <div class="puzzle-piece-container absolute-puzzle-03">
              <svg height="220px" class="puzzlepiece03" v-html="puzzlepiece03"></svg>

              <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

              <div class="puzzle-piece">
                <p>{{ currentRound.letter }}</p>
              </div>
            </div>

            <div class="puzzle-piece-container absolute-puzzle-04">
              <svg height="188px" class="puzzlepiece04" v-html="puzzlepiece04"></svg>

              <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

              <div class="puzzle-piece">
                <p>{{ currentRound.letter }}</p>
              </div>
            </div>

            <div class="puzzle-piece-container absolute-puzzle-05">
              <svg height="235px" class="puzzlepiece05" v-html="puzzlepiece05"></svg>

              <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

              <div class="puzzle-piece">
                <p>{{ currentRound.letter }}</p>
              </div>
            </div>

            <div class="puzzle-piece-container absolute-puzzle-06">
              <svg height="154px" class="puzzlepiece06" v-html="puzzlepiece06"></svg>

              <svg width="80px" class="puzzle-outline" v-html="puzzlingoutline"></svg>

              <div class="puzzle-piece">
                <p>{{ currentRound.letter }}</p>
              </div>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';
import { shuffle, range } from 'lodash';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

import puzzlepiece01 from 'assets/svg/puzzling-letters/puzzle-piece-01.svg';
import puzzlepiece02 from 'assets/svg/puzzling-letters/puzzle-piece-02.svg';
import puzzlepiece03 from 'assets/svg/puzzling-letters/puzzle-piece-03.svg';
import puzzlepiece04 from 'assets/svg/puzzling-letters/puzzle-piece-04.svg';
import puzzlepiece05 from 'assets/svg/puzzling-letters/puzzle-piece-05.svg';
import puzzlepiece06 from 'assets/svg/puzzling-letters/puzzle-piece-06.svg';
import puzzlingoutline from 'assets/svg/puzzling-letters/puzzling-letter-outline.svg';

import ActivityStage from 'components/activity/ActivityStage.vue';

export default {
  components: {
    ActivityStage
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'ObjectShuffle',
      sfx: null,
      puzzlepiece01,
      puzzlepiece02,
      puzzlepiece03,
      puzzlepiece04,
      puzzlepiece05,
      puzzlepiece06,
      puzzlingoutline,
      puzzlePieces: []
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      const steps = roundSetup.rounds;
      await this.loadImages(steps);
      this.sfx = await this.loadSounds(steps);
    },

    async loadImages(steps) {
      steps.forEach(({ answerImage }) => this.queueImage(answerImage.image.src));
      return this.downloadImages();
    },

    async loadSounds(steps) {
      const soundPaths = steps.map(({ answerAudio }) => answerAudio.audio.src);
      this.queueAudio(soundPaths);

      const cache = await this.downloadAudio();
      return steps.map(({ answerAudio }) => cache.get(answerAudio.audio.src));
    },

    getActivityState() {
      const {
        rounds,
        keyboard: { characters }
      } = this.activityData;
      const written = characters ? 'Hold the target. Type each letter.' : 'Type each letter.';
      const voice = characters
        ? `${this.voiceInstructionsPrefix}/puzzlesurprise-${this.getRandomVoice()}-1.mp3`
        : `${this.voiceInstructionsPrefix}/shuffledlettersnth-${this.getRandomVoice()}-1.mp3`;

      this.puzzlePieces = this.generateRandomPieceArray();

      return {
        rounds,
        stepIndicator: 'rounds',
        instructions: {
          written,
          voice
        }
      };
    },

    getKeyboardConfig() {
      const { characters, keyboardType, roofs } = this.activityData.keyboard;
      const isTouchHold = !!characters;
      const showTargets = this.isMobile && !(this.ipadWithoutExternalKeyboard && isTouchHold);

      return {
        showTargets,
        keyboardType,
        roofs
      };
    },

    // generate a random array used to hide the puzzle pieces
    generateRandomPieceArray() {
      return shuffle(range(0, 6));
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
        TweenMax.to('.puzzling-letters-title', 0, { css: { autoAlpha: 0 } });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.rerenderScreenReaderAlert();
      this.hidePuzzlePiece();

      this.captureKeyboardAttemptSegment(attempt, true);

      if (this.puzzlePieces.length === 0) {
        this.disableKeyboard();
        this.handleStepEnd();
      } else {
        this.setCurrentKeyboardingSegment('');
        this.setTotalKeyboardingSegment(attempt.character);
      }
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentRound']['letter'];
    },

    hidePuzzlePiece() {
      const allPieces = document.getElementsByClassName('puzzle-piece-container');
      const index = this.puzzlePieces[0];
      TweenMax.to(allPieces[index], 0.25, { css: { autoAlpha: 0 } });
      this.puzzlePieces = this.puzzlePieces.slice(1);
    },

    handleStepEnd() {
      const { answerAudio } = this.currentRound;
      this.sndBell();
      TweenMax.to('.puzzling-letters-title', 0.5, { css: { autoAlpha: 1 } });
      if (answerAudio?.audio?.src) {
        this.sfx[this.progress.roundIndex].play();
        TweenMax.delayedCall(2, this.finishStep);
      } else {
        TweenMax.delayedCall(0.5, this.finishStep);
      }
    },

    finishStep() {
      const isLastRound = this.progress.roundIndex >= this.activityData.rounds.length - 1;
      if (isLastRound) {
        this.handleRoundEnd();
      } else {
        TweenMax.to(document.getElementsByClassName('puzzle-piece-container'), 0, {
          css: { autoAlpha: 1 }
        });
        TweenMax.to('.puzzling-letters-title', 0, { css: { autoAlpha: 0 } });
        this.setupNewRound({ stepCallback: this.stepCallback });
        this.enableKeyboard();
        this.puzzlePieces = this.generateRandomPieceArray();
      }
    },

    handleRoundEnd() {
      TweenMax.delayedCall(0.5, this.completeActivity);
    }
  },
  watch: {
    currentRound(newCurrentRound) {
      let instructions = `Type the ${newCurrentRound?.letter} key.`;

      this.setTotalKeyboardingSegment(newCurrentRound?.letter);
      this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
    }
  }
};
</script>

<style lang="scss" scoped>
.puzzling-letters-container {
  position: relative;
  max-width: 1024px;
  min-width: 612px;
  margin: 0 auto;
  width: 100%;

  .puzzling-letters-title {
    font-size: 24px;
    height: 45px;
    position: absolute;
    text-align: center;

    width: 100%;

    h2 {
      font-weight: bold;
      margin: 0px;
    }
  }

  .puzzle-letters-container,
  .background-image-container {
    display: block;
    position: absolute;
    left: 50%;
    width: 500px;
    /* 60% */
    height: 330px;
    /* 40% */
    transform: translateX(-50%);
    background-color: transparent;
    z-index: 300;

    .puzzle-piece-container {
      position: absolute;
      display: block;
    }

    .absolute-puzzle-01 {
      top: 0;
      left: 0;

      p {
        height: 60px;
        margin: 40px 37px;
        padding-top: 20px;
      }

      .puzzle-outline {
        position: absolute;
        top: 43px;
        left: 44px;
        width: 80px;
        height: 80px;
      }
    }

    .absolute-puzzle-02 {
      top: 0;
      left: 127px;

      p {
        width: 96px;
        height: 60px;
        margin: 40px 75px;
        padding-top: 20px;
      }

      .puzzle-outline {
        position: absolute;
        top: 40px;
        left: 82px;
        width: 80px;
        height: 80px;
      }
    }

    .absolute-puzzle-03 {
      top: -1px;
      left: 332px;

      p {
        width: 96px;
        height: 60px;
        margin: 40px 37px;
        padding-top: 20px;
      }

      .puzzle-outline {
        position: absolute;
        top: 43px;
        left: 45px;
        width: 80px;
        height: 80px;
      }
    }

    .absolute-puzzle-04 {
      top: 147px;
      left: 0;

      p {
        width: 96px;
        height: 60px;
        margin: 60px 38px;
        padding-top: 20px;
      }

      .puzzle-outline {
        position: absolute;
        top: 63px;
        left: 45px;
        width: 80px;
        height: 80px;
      }
    }

    .absolute-puzzle-05 {
      top: 103px;
      left: 167px;

      p {
        width: 96px;
        height: 60px;
        margin: 103px 35px;
        padding-top: 20px;
      }

      .puzzle-outline {
        position: absolute;
        top: 103px;
        left: 43px;
        width: 80px;
        height: 80px;
      }
    }

    .absolute-puzzle-06 {
      top: 163px;
      left: 283px;

      p {
        width: 96px;
        height: 60px;
        margin: 40px 84px;
        padding-top: 20px;
      }

      .puzzle-outline {
        position: absolute;
        top: 45px;
        left: 90px;
        width: 80px;
        height: 80px;
      }
    }

    // Puzzle Pieces Images
    .puzzlepiece01 {
      width: 170px;
    }

    .puzzlepiece02 {
      width: 247px;
    }

    .puzzlepiece03 {
      width: 162px;
    }

    .puzzlepiece04 {
      width: 210px;
    }

    .puzzlepiece05 {
      width: 167px;
    }

    .puzzlepiece06 {
      width: 220px;
    }

    // Puzzle Piece Text
    .puzzle-piece {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: transparent;

      p {
        display: block;
        text-align: center;
        font-size: 42px;
        color: black;
      }
    }

    .puzzle-outline {
      display: none;
    }

    .active {
      display: block;
    }
  }

  .background-image-container {
    height: 310px;
    top: 60px;
    width: 490px;
    z-index: 299;
  }

  .puzzle-letters-container {
    top: 45px;
  }
}

.chrome-os {
  .background-image-container,
  .puzzle-letters-container {
    transform: translateX(-50%) scale(0.75);
  }
}

.tablet {
  .background-image-container,
  .puzzle-letters-container {
    transform: translateX(-50%) scale(0.8);
  }

  .puzzle-letters-container {
    top: 32px;
  }

  .background-image-container {
    top: 47px;
  }
}
</style>
